import { FC, useEffect, useMemo } from 'react';

import _ from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';

import { FormLabel } from 'components/FormLabel/FormLabel';
import { If } from 'components/Generics';
import RhfSelect from 'components/Generics/Rhf/RhfSelect';
import RhfTeethToExtract from 'components/TreatmentPlanningInstructions/TreatmentSpecifications/TeethToExtract/RhfTeethToExtract/RhfTeethToExtract';
import {
  OCCLUSION_CORRECTION,
  OCCLUSION_CORRECTION_OPTIONS,
  occlusionCorrectionRequiresTeeth
} from 'constants/TPVFormOptions';
import { TeethExtraction } from 'types/TreatmentPlanning.types';
import { translate } from 'utils';
import { getSelectedOption } from 'utils/tpvUtils';

const emptyTeeth: TeethExtraction = {
  rightTop: [],
  rightBottom: [],
  leftTop: [],
  leftBottom: []
};

/** Missing form value → None so the select always matches an option. */
const toCanonicalOcclusion = (
  v: string | undefined | null
): OCCLUSION_CORRECTION => {
  if (v == null || v === '') return OCCLUSION_CORRECTION.NONE;
  if (
    v === OCCLUSION_CORRECTION.NONE ||
    v === OCCLUSION_CORRECTION.ANTERIOR ||
    v === OCCLUSION_CORRECTION.POSTERIOR
  ) {
    return v;
  }
  return OCCLUSION_CORRECTION.NONE;
};

type Props = {
  disabled?: boolean;
  inputName: string;
  teethFieldName: string;
  label: string;
  /** ZenyumClear-only; rendered only when `disablePosteriorOption` is true. */
  helperText?: string;
  animateLabel?: string;
  /** When true, Posterior is shown but cannot be selected (ZenyumClear). */
  disablePosteriorOption?: boolean;
};

const OcclusionCorrectionInput: FC<Props> = ({
  inputName,
  teethFieldName,
  disabled,
  label,
  helperText,
  animateLabel,
  disablePosteriorOption = false
}) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors }
  } = useFormContext();
  const value = watch(inputName);
  const canonicalOcclusion = useMemo(
    () => toCanonicalOcclusion(value),
    [value]
  );

  useEffect(() => {
    if (canonicalOcclusion !== value) {
      setValue(inputName, canonicalOcclusion, {
        shouldDirty: false,
        shouldValidate: false
      });
    }
  }, [canonicalOcclusion, value, inputName, setValue]);

  const occlusionSelectOptions = useMemo(
    () =>
      disablePosteriorOption
        ? OCCLUSION_CORRECTION_OPTIONS.map((option) =>
            option.value === OCCLUSION_CORRECTION.POSTERIOR
              ? {
                  ...option,
                  isDisabled: true,
                  className: 'text-[#D1D5DB]'
                }
              : option
          )
        : OCCLUSION_CORRECTION_OPTIONS,
    [disablePosteriorOption]
  );

  const showTeethChart = occlusionCorrectionRequiresTeeth(canonicalOcclusion);

  return (
    <div className='relative'>
      <Controller
        control={control}
        name={inputName}
        defaultValue={OCCLUSION_CORRECTION.NONE}
        render={({ field }) => (
          <>
            <FormLabel
              label={label}
              error={
                !!_.get(errors, inputName) || !!_.get(errors, teethFieldName)
              }
              name={inputName}
            />
            <div className='md:w-325px mt-4'>
              <RhfSelect
                name={inputName}
                options={occlusionSelectOptions}
                onChange={(data) => {
                  const v = data?.value;
                  field.onChange(v);
                  if (v === OCCLUSION_CORRECTION.NONE) {
                    setValue(teethFieldName, emptyTeeth, { shouldDirty: true });
                  }
                }}
                isDisabled={disabled}
                inputProps={{
                  controlShouldRenderValue: true,
                  selectedValue: getSelectedOption(
                    canonicalOcclusion,
                    occlusionSelectOptions
                  )
                }}
              />
            </div>
            {disablePosteriorOption && helperText ? (
              <p className='text-14px text-GRAY3 mt-2'>{helperText}</p>
            ) : null}
            <If condition={showTeethChart}>
              <div className='mt-6'>
                <RhfTeethToExtract
                  showError={false}
                  name={teethFieldName}
                  title={translate('occlusionCorrection.teethToCorrect')}
                  titleClassName='text-BLUE_4'
                  disabled={disabled}
                  animateLabel={animateLabel ?? teethFieldName}
                />
              </div>
            </If>
          </>
        )}
      />
    </div>
  );
};

export default OcclusionCorrectionInput;
